import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { usePaginatedApi, useAsyncOperation } from '../hooks/useApi';
import { loadsAPI, clientsAPI, systemConfigAPI } from '../services/api';
import { formatWeight, formatDateTime, getTransactionTypeDisplay, downloadFile } from '../utils/helpers';
import { buildLoadWeighPassListing } from '../utils/loadWeighPasses';
import Table from '../components/Table';
import Button from '../components/Button';
import Input, { Select } from '../components/Input';
import Modal, { ConfirmModal } from '../components/Modal';
import EditLoadModal from '../components/EditLoadModal';
import AddTicketModal from '../components/AddTicketModal';
import { extractTsMetaFromNotes, stripTsMetaFromNotes, getSupplementaryTaxOption } from '../utils/supplementaryTax';
import { useToast } from '../contexts/ToastContext';
import { useAuth } from '../contexts/AuthContext';
import { useMailing } from '../contexts/MailingContext';
import { CLIENT_PRE_TARE_REQUESTS_CONFIG_KEY } from '../constants/systemConfigKeys';
import {
  MagnifyingGlassIcon,
  FunnelIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  DocumentArrowDownIcon,
  EnvelopeIcon,
  CheckIcon,
  PlusIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';

const RequestedLoadsPage = () => {
  const { t } = useTranslation();
  const { isManagerOrAdmin, isAdmin, isOperator, isDriver, isPeseur, user } = useAuth();
  const { mailingEnabled } = useMailing();
  const defaultRequestStatuses = 'REQUESTED,REJECTED';
  
  const [filters, setFilters] = useState({
    search: '',
    clientId: '',
    transactionType: '',
    status: defaultRequestStatuses,
    startDate: '',
    endDate: '',
    ...(isOperator() ? { operatorId: user?.id } : {}),
  });
  
  const [showFilters, setShowFilters] = useState(false);
  const [selectedLoad, setSelectedLoad] = useState(null);
  const [showLoadModal, setShowLoadModal] = useState(false);
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [emailAddress, setEmailAddress] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [preTareRequestsAllowed, setPreTareRequestsAllowed] = useState(false);
  const [preTareEligibilityLoading, setPreTareEligibilityLoading] = useState(false);
  const [isEligibleBillingType, setIsEligibleBillingType] = useState(false);

  useEffect(() => {
    if (!mailingEnabled) setEmailModalOpen(false);
  }, [mailingEnabled]);

  useEffect(() => {
    let cancelled = false;
    if (!isOperator() || !user?.clientId) {
      setPreTareRequestsAllowed(false);
      setIsEligibleBillingType(false);
      return () => {
        cancelled = true;
      };
    }

    const loadEligibility = async () => {
      try {
        setPreTareEligibilityLoading(true);
        const [cfgRes, clientRes] = await Promise.allSettled([
          systemConfigAPI.getByKey(CLIENT_PRE_TARE_REQUESTS_CONFIG_KEY),
          clientsAPI.getById(user.clientId)
        ]);
        if (cancelled) return;

        const featureAllowed =
          cfgRes.status === 'fulfilled'
            ? Boolean(cfgRes.value?.data?.value)
            : cfgRes.reason?.response?.status === 404
              ? false
              : (() => {
                  throw cfgRes.reason;
                })();
        const billingType =
          clientRes.status === 'fulfilled'
            ? String(clientRes.value?.data?.client?.billingType || clientRes.value?.data?.billingType || '')
            : '';
        setPreTareRequestsAllowed(featureAllowed);
        setIsEligibleBillingType(['CREDIT', 'PAYING'].includes(billingType));
      } catch (error) {
        if (!cancelled) {
          setPreTareRequestsAllowed(false);
          setIsEligibleBillingType(false);
        }
      } finally {
        if (!cancelled) setPreTareEligibilityLoading(false);
      }
    };

    loadEligibility();
    return () => {
      cancelled = true;
    };
  }, [isOperator, user?.clientId]);
  
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const toast = useToast();

  // Fetch loads with pagination - always with REQUESTED status
  const {
    data: loads,
    pagination,
    loading,
    updateParams,
    goToPage,
    refresh,
  } = usePaginatedApi(loadsAPI.getAll, filters);

  // Fetch clients for filter dropdown
  const { data: clients } = usePaginatedApi(clientsAPI.getAll, { limit: 100 });

  // Async operations
  const { execute: executeAsync } = useAsyncOperation();

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const applyFilters = () => {
    updateParams(filters);
  };

  const resetFilters = () => {
    const resetFilters = {
      search: '',
      clientId: '',
      transactionType: '',
      status: defaultRequestStatuses,
      startDate: '',
      endDate: '',
      ...(isOperator() ? { operatorId: user?.id } : {}),
    };
    setFilters(resetFilters);
    updateParams(resetFilters);
  };

  const handleView = (load) => {
    setSelectedLoad(load);
    setShowLoadModal(true);
  };

  const handleEdit = async (load) => {
    try {
      const response = await loadsAPI.getById(load.id);
      setSelectedLoad(response.data.load);
      setShowEditModal(true);
    } catch (error) {
      console.error('Error fetching load for edit:', error);
      setSelectedLoad(load);
      setShowEditModal(true);
    }
  };

  const handleDelete = (load) => {
    setSelectedLoad(load);
    setShowDeleteModal(true);
  };

  const handleReject = (load) => {
    setSelectedLoad(load);
    setRejectReason('');
    setShowRejectModal(true);
  };

  const handleSendEmail = (load) => {
    setSelectedLoad(load);
    setEmailAddress(load.client?.contactEmail || '');
    setEmailModalOpen(true);
  };

  const sendTicketEmail = async () => {
    if (!emailAddress) {
      toast.error(t('loads.emailAddressRequired'));
      return;
    }

    await executeAsync(
      async () => {
        await loadsAPI.sendTicket(selectedLoad.id, { email: emailAddress });
        setEmailModalOpen(false);
        setEmailAddress('');
      },
      {
        showSuccessToast: true,
        successMessage: t('loads.ticketSentSuccess'),
      }
    );
  };

  const downloadTicket = async (load) => {
    await executeAsync(
      async () => {
        const currentLanguage = localStorage.getItem('i18nextLng') || 'fr';
        const response = await loadsAPI.downloadTicket(load.id, currentLanguage);
        downloadFile(response.data, `ticket-${load.ticketNumber}.pdf`, 'application/pdf');
      },
      {
        showSuccessToast: true,
        successMessage: t('loads.ticketDownloadedSuccess'),
      }
    );
  };

  const handleDeleteConfirm = async () => {
    await executeAsync(
      async () => {
        await loadsAPI.delete(selectedLoad.id);
        setShowDeleteModal(false);
        setSelectedLoad(null);
        refresh();
      },
      {
        showSuccessToast: true,
        successMessage: t('loads.crud.loadDeleted'),
      }
    );
  };

  const handleRejectConfirm = async () => {
    if (!selectedLoad?.id) return;
    await executeAsync(
      async () => {
        await loadsAPI.updateStatus(selectedLoad.id, {
          status: 'REJECTED',
          rejectionReason: rejectReason || undefined
        });
        setShowRejectModal(false);
        setRejectReason('');
        setSelectedLoad(null);
        refresh();
      },
      {
        showSuccessToast: true,
        successMessage: t('loads.requestRejectedSuccess')
      }
    );
  };

  const handleStatusChange = async (load, newStatusOrPayload) => {
    const targetStatus =
      typeof newStatusOrPayload === 'string'
        ? newStatusOrPayload
        : String(newStatusOrPayload?.status || '');
    try {
      await executeAsync(
        async () => {
          try {
            await loadsAPI.updateStatus(load.id, newStatusOrPayload);
            refresh();
          } catch (error) {
            if (error.response?.status === 400 && targetStatus === 'LOADED' && error.response.data) {
              const details = error.response.data.details;
              if (details) {
                const missing = [];
                if (details.emptyWeightMissing) missing.push(t('weighing.emptyWeight'));
                if (details.loadedWeightMissing) missing.push(t('weighing.loadedWeight'));
                if (missing.length > 0) {
                  error.response.data.message =
                    t('loads.cannotMarkLoadedMissingWeights', {
                      missing: missing.join(` ${t('common.and')} `)
                    });
                }
              }
            }
            throw error;
          }
        },
        {
          showSuccessToast: true,
          successMessage: t('loads.actions.statusUpdated'),
        }
      );
    } catch {
      /* executeAsync already showed toast */
    }
  };

  // Helper function for status badge colors
  const getStatusBadgeColor = (status) => {
    switch (status) {
      case 'REQUESTED': return 'bg-yellow-100 text-yellow-800';
      case 'REJECTED': return 'bg-red-100 text-red-800';
      case 'ACCEPTED': return 'bg-blue-100 text-blue-800';
      case 'LOADED': return 'bg-purple-100 text-purple-800';
      case 'DELIVERED': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const columns = [
    {
      key: 'loadDate',
      title: t('loads.loadDate'),
      render: (value) => formatDateTime(value),
      sortable: true,
    },
    // Conditionally include client column for Admin/Manager
    ...(isManagerOrAdmin() ? [{
      key: 'client',
      title: t('tables.client'),
      render: (value, row) => row.client?.companyName,
    }] : []),
    {
      key: 'truck',
      title: t('tables.truck'),
      render: (value, row) => (
        <div>
          <div className="font-medium">{row.truck?.plateNumber}</div>
          <div className="text-sm text-gray-500">{row.truck?.driverName}</div>
        </div>
      ),
    },
    {
      key: 'product',
      title: t('tables.product'),
      render: (value, row) => row.product?.name,
    },
    {
      key: 'port',
      title: t('weighing.port'),
      render: (value, row) => row.port?.name,
    },
    {
      key: 'status',
      title: t('loads.status'),
      render: (value, row) => (
        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusBadgeColor(row.status)}`}>
          {t(`loads.statuses.${row.status?.toLowerCase()}`)}
        </span>
      ),
    },
    // Hide transaction type column for Admin, Manager, and Driver users
    ...(!isAdmin() && !isManagerOrAdmin() && !isDriver() ? [{
      key: 'transactionType',
      title: t('weighing.transactionType'),
      render: (value) => getTransactionTypeDisplay(value),
    }] : []),
    {
      key: 'actions',
      title: t('tables.actions'),
      sticky: true,
      width: '200px',
      render: (value, row) => (
        <div className="flex items-center space-x-2">
          <Button size="sm" variant="ghost" onClick={() => handleView(row)} title={t('common.view')}>
            <EyeIcon className="h-4 w-4" />
          </Button>
          
          {isAdmin() && !isOperator() && (
            <Button size="sm" variant="ghost" onClick={() => handleEdit(row)} title={t('common.edit')}>
              <PencilIcon className="h-4 w-4" />
            </Button>
          )}
          
          {(isManagerOrAdmin() || (isOperator() && row.operatorId === user?.id && ['REQUESTED', 'REJECTED'].includes(row.status))) &&
            row.canDeleteNoCertificateOrBill === true && (
            <Button size="sm" variant="ghost" onClick={() => handleDelete(row)} title={t('common.delete')} className="text-red-600 hover:text-red-700">
              <TrashIcon className="h-4 w-4" />
            </Button>
          )}
          
          {row.ticket && (
            <Button size="sm" variant="ghost" onClick={() => downloadTicket(row)} title={t('loads.printTicket')}>
              <DocumentArrowDownIcon className="h-4 w-4" />
            </Button>
          )}
          
          {row.ticket && mailingEnabled && (
            <Button size="sm" variant="ghost" onClick={() => handleSendEmail(row)} title={t('common.sendEmail')}>
              <EnvelopeIcon className="h-4 w-4" />
            </Button>
          )}
          
          {/* Accept: managers/admins (not client-operator) or peuseur */}
          {['REQUESTED', 'REJECTED'].includes(row.status) &&
            ((isManagerOrAdmin() && !isOperator()) || isPeseur()) && (
            <Button 
              size="sm" 
              variant="ghost" 
              onClick={() => handleStatusChange(row, 'ACCEPTED')}
              title={t('loads.actions.acceptLoad')}
              className="text-blue-600 hover:text-blue-700"
            >
              <CheckIcon className="h-4 w-4" />
            </Button>
          )}
          {['REQUESTED', 'ACCEPTED'].includes(row.status) &&
            isManagerOrAdmin() &&
            !isOperator() && (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => handleReject(row)}
              title={t('loads.actions.rejectLoad')}
              className="text-red-700 hover:text-red-800"
            >
              <XMarkIcon className="h-4 w-4" />
            </Button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {t('loads.requestedLoadsPageTitle')}
          </h1>
          <p className="text-gray-600">
            {t('loads.newRequestsDesc')}
          </p>
        </div>
        {isOperator() && (
          <Button
            onClick={() => setShowAddModal(true)}
            disabled={preTareEligibilityLoading || !preTareRequestsAllowed || !isEligibleBillingType}
            title={
              !preTareRequestsAllowed
                ? t('systemConfig.clients.allowPreTareRequestsHint')
                : !isEligibleBillingType
                  ? t('loads.onlyEligibleClientsCanRequest')
                  : undefined
            }
          >
            <PlusIcon className="h-4 w-4 mr-2" />
            {t('loads.crud.requestLoad')}
          </Button>
        )}
      </div>

      {/* Filters */}
      <div className="flex justify-between items-center">
        <Button variant="outline" onClick={() => setShowFilters(!showFilters)}>
          <FunnelIcon className="h-4 w-4 mr-2" />
          {showFilters ? t('loads.hideFilters') : t('loads.showFilters')}
        </Button>
      </div>

      {/* Filters */}
      {showFilters && (
        <div className="card">
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4 mb-4">
            <Input
              placeholder={t('loads.searchPlaceholder')}
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
            />
            
            {isManagerOrAdmin() && (
              <Select
                placeholder={t('loads.selectClient')}
                value={filters.clientId}
                onChange={(e) => handleFilterChange('clientId', e.target.value)}
                options={clients?.map(client => ({
                  value: client.id,
                  label: client.companyName
                })) || []}
              />
            )}
            
            {/* Hide transaction type filter for Admin, Manager, and Driver users */}
            {!isAdmin() && !isManagerOrAdmin() && !isDriver() && (
              <Select
                placeholder={t('loads.selectTransactionType')}
                value={filters.transactionType}
                onChange={(e) => handleFilterChange('transactionType', e.target.value)}
                options={[
                  { value: 'IMPORT', label: t('weighing.import') },
                  { value: 'EXPORT', label: t('weighing.export') },
                ]}
              />
            )}
            <Select
              placeholder={t('loads.selectStatus')}
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              options={[
                { value: 'REQUESTED,REJECTED', label: `${t('loads.statuses.requested')} + ${t('loads.statuses.rejected')}` },
                { value: 'REQUESTED', label: t('loads.statuses.requested') },
                { value: 'REJECTED', label: t('loads.statuses.rejected') },
                { value: 'ACCEPTED', label: t('loads.statuses.accepted') }
              ]}
            />
            
            <Input
              type="date"
              placeholder={t('loads.startDate')}
              value={filters.startDate}
              onChange={(e) => handleFilterChange('startDate', e.target.value)}
            />
            
            <Input
              type="date"
              placeholder={t('loads.endDate')}
              value={filters.endDate}
              onChange={(e) => handleFilterChange('endDate', e.target.value)}
            />
          </div>
          
          <div className="flex space-x-3">
            <Button onClick={applyFilters}>
              <MagnifyingGlassIcon className="h-4 w-4 mr-2" />
              {t('common.filter')}
            </Button>
            <Button variant="outline" onClick={resetFilters}>
              {t('common.clear')}
            </Button>
          </div>
        </div>
      )}

      {/* Table */}
      <Table
        columns={columns}
        data={loads || []}
        loading={loading}
        pagination={pagination}
        onPageChange={goToPage}
        emptyMessage={isOperator() ? t('loads.noMyRequests') : t('loads.noRequestedLoads')}
      />

      {/* Load Details Modal */}
      <Modal
        isOpen={showLoadModal}
        onClose={() => setShowLoadModal(false)}
        title={t('loads.loadDetails')}
        size="xl"
      >
        {selectedLoad && (
          <div className="space-y-6">
            {/* Basic Information */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-3">{t('loads.basicInformation')}</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">{t('loads.ticketNumber')}</label>
                  <p className="text-sm text-gray-900">#{selectedLoad.ticket?.ticketNumber || t('common.notAvailable')}</p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700">{t('loads.loadDate')}</label>
                  <p className="text-sm text-gray-900">{formatDateTime(selectedLoad.loadDate)}</p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700">{t('loads.status')}</label>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadgeColor(selectedLoad.status)}`}>
                    {t(`loads.statuses.${selectedLoad.status?.toLowerCase()}`)}
                  </span>
                </div>
                
                {/* Hide transaction type for Admin, Manager, and Driver users */}
                {!isAdmin() && !isManagerOrAdmin() && !isDriver() && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700">{t('weighing.transactionType')}</label>
                    <p className="text-sm text-gray-900">{getTransactionTypeDisplay(selectedLoad.transactionType)}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Client & Truck Information */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-3">{t('loads.clientTruckInfo')}</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">{t('tables.client')}</label>
                  <p className="text-sm text-gray-900">{selectedLoad.client?.companyName}</p>
                  <p className="text-xs text-gray-500">{selectedLoad.client?.contactName}</p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700">{t('tables.truck')}</label>
                  <p className="text-sm text-gray-900">{selectedLoad.truck?.plateNumber}</p>
                  <p className="text-xs text-gray-500">{selectedLoad.truck?.driverName}</p>
                </div>
              </div>
            </div>

            {/* Product & Location Information */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-3">{t('loads.productLocationInfo')}</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">{t('tables.product')}</label>
                  <p className="text-sm text-gray-900">{selectedLoad.product?.name}</p>
                  <p className="text-xs text-gray-500">{selectedLoad.product?.description}</p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700">{t('weighing.port')}</label>
                  <p className="text-sm text-gray-900">{selectedLoad.port?.name}</p>
                  <p className="text-xs text-gray-500">{selectedLoad.port?.location}</p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700">{t('weighing.weighbridge')}</label>
                  <p className="text-sm text-gray-900">{selectedLoad.weighbridge?.name}</p>
                  <p className="text-xs text-gray-500">{selectedLoad.weighbridge?.location}</p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700">{t('tables.operator')}</label>
                  <p className="text-sm text-gray-900">
                    {selectedLoad.operator ? 
                      `${selectedLoad.operator.firstName} ${selectedLoad.operator.lastName}` : 
                      t('common.notAvailable')
                    }
                  </p>
                  <p className="text-xs text-gray-500">{selectedLoad.operator?.username}</p>
                </div>
              </div>
            </div>

            {/* Weight Information */}
            {(() => {
              const weighPassRows = buildLoadWeighPassListing(selectedLoad, t);
              const hasSummary =
                selectedLoad.emptyWeight ||
                selectedLoad.loadedWeight ||
                selectedLoad.netWeight ||
                selectedLoad.truck?.emptyWeight;
              if (!weighPassRows.length && !hasSummary) return null;
              return (
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-3">{t('weighing.weightInformation')}</h3>
                  <div className="bg-gray-50 p-4 rounded-lg space-y-4">
                    {weighPassRows.length > 0 && (
                      <div>
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                          {t('loads.modal.passListingTitle')}
                        </p>
                        <table className="w-full text-sm border border-gray-200 rounded-md overflow-hidden bg-white">
                          <thead className="bg-gray-100 text-xs text-gray-600">
                            <tr>
                              <th className="text-left font-semibold px-3 py-2 border-b border-gray-200">
                                {t('loads.modal.passTableColPass')}
                              </th>
                              <th className="text-right font-semibold px-3 py-2 border-b border-gray-200 whitespace-nowrap">
                                {t('loads.modal.passTableColWeight')}
                              </th>
                              <th className="text-right font-semibold px-3 py-2 border-b border-gray-200 whitespace-nowrap">
                                {t('loads.modal.dateTime')}
                              </th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100">
                            {weighPassRows.map((row) => (
                              <tr key={row.key}>
                                <td className="px-3 py-2.5 font-medium text-gray-800 align-middle">{row.label}</td>
                                <td className="px-3 py-2.5 text-right font-semibold text-gray-900 tabular-nums align-middle whitespace-nowrap">
                                  {row.weight}
                                </td>
                                <td className="px-3 py-2.5 text-right text-gray-600 tabular-nums align-middle whitespace-nowrap text-xs sm:text-sm">
                                  {row.date}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                    {weighPassRows.length > 0 && (
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                        {t('loads.modal.weightSummary')}
                      </p>
                    )}
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">{t('weighing.emptyWeight')}</label>
                        <p className="text-sm text-gray-900">
                          {selectedLoad.emptyWeight
                            ? formatWeight(selectedLoad.emptyWeight, 'ton', { noUnit: true })
                            : selectedLoad.truck?.emptyWeight
                              ? formatWeight(selectedLoad.truck.emptyWeight, 'ton', { noUnit: true })
                              : t('common.notAvailable')}
                        </p>
                        {selectedLoad.truck?.emptyWeight && !selectedLoad.emptyWeight && (
                          <p className="text-xs text-gray-500 mt-1">{t('loads.modal.fromTruckTare')}</p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700">{t('weighing.loadedWeight')}</label>
                        <p className="text-sm text-gray-900">
                          {selectedLoad.loadedWeight
                            ? formatWeight(selectedLoad.loadedWeight, 'ton', { noUnit: true })
                            : t('common.notAvailable')}
                        </p>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700">{t('weighing.netWeight')}</label>
                        <p className="text-sm text-gray-900 font-medium">
                          {selectedLoad.netWeight ? formatWeight(selectedLoad.netWeight, 'ton', { noUnit: true }) : t('common.notAvailable')}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* TS Details (always show; "none" when empty) + Notes */}
            {(() => {
              const tsMeta = selectedLoad.status === 'LOADED' ? extractTsMetaFromNotes(selectedLoad.notes) : null;
              const cleanNotes = stripTsMetaFromNotes(selectedLoad.notes);
              const tsOption = tsMeta ? getSupplementaryTaxOption(tsMeta.optionCode) : null;
              return (
                <>
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-2">{t('loads.ts.detail.title')}</h3>
                    {tsMeta ? (
                      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                        <dl className="grid grid-cols-3 gap-4">
                          <div>
                            <dt className="text-xs text-amber-700 font-medium">{t('loads.ts.detail.option')}</dt>
                            <dd className="text-sm text-amber-900 mt-1">
                              {tsOption ? t(tsOption.labelKey) : tsMeta.description}
                            </dd>
                          </div>
                          <div>
                            <dt className="text-xs text-amber-700 font-medium">{t('loads.ts.detail.hours')}</dt>
                            <dd className="text-sm text-amber-900 mt-1">{tsMeta.hours} h</dd>
                          </div>
                          <div>
                            <dt className="text-xs text-amber-700 font-medium">{t('loads.ts.detail.totalAmount')}</dt>
                            <dd className="text-sm font-semibold text-amber-900 mt-1">
                              {Number(tsMeta.totalAmount).toLocaleString()} FCFA
                            </dd>
                          </div>
                        </dl>
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500 bg-gray-50 border border-gray-200 rounded-lg p-3">
                        {selectedLoad.status === 'LOADED' ? t('loads.ts.noSupplementaryTax') : t('loads.ts.tsSetWhenMarkedLoaded')}
                      </p>
                    )}
                  </div>
                  {cleanNotes && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">{t('weighing.notes')}</label>
                      <p className="text-sm text-gray-900 bg-gray-50 p-3 rounded-md">{cleanNotes}</p>
                    </div>
                  )}
                </>
              );
            })()}
          </div>
        )}
      </Modal>

      {/* Email Modal */}
      <Modal
        isOpen={emailModalOpen}
        onClose={() => setEmailModalOpen(false)}
        title={t('loads.sendTicketByEmail')}
      >
        <div className="space-y-4">
          <Input
            label={t('common.emailAddress')}
            type="email"
            value={emailAddress}
            onChange={(e) => setEmailAddress(e.target.value)}
            placeholder={t('common.enterEmailAddress')}
          />
          
          <div className="flex justify-end space-x-3">
            <Button variant="outline" onClick={() => setEmailModalOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button onClick={sendTicketEmail}>
              {t('common.sendEmail')}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Edit Load Modal */}
      <EditLoadModal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        onSuccess={() => {
          setShowEditModal(false);
          refresh();
        }}
        load={selectedLoad}
      />

      <AddTicketModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSuccess={() => {
          setShowAddModal(false);
          refresh();
        }}
      />

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDeleteConfirm}
        title={isOperator() ? t('loads.deleteRequestTitle') : t('loads.crud.deleteLoad')}
        message={isOperator() ? t('loads.deleteRequestConfirm') : t('loads.crud.deleteConfirmGeneric')}
        confirmText={t('common.delete')}
        variant="danger"
      />

      <Modal
        isOpen={showRejectModal}
        onClose={() => setShowRejectModal(false)}
        title={t('loads.rejectRequestTitle')}
        footer={
          <>
            <Button variant="outline" onClick={() => setShowRejectModal(false)}>
              {t('common.cancel')}
            </Button>
            <Button variant="danger" onClick={handleRejectConfirm}>
              {t('loads.actions.rejectLoad')}
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <label className="block text-sm font-medium text-gray-700">
            {t('loads.rejectRequestReason')}
          </label>
          <textarea
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            placeholder={t('loads.rejectRequestReasonPlaceholder')}
            rows={4}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
          />
        </div>
      </Modal>
    </div>
  );
};

export default RequestedLoadsPage;
